import React from 'react';
import { 
    TextField, 
    Checkbox, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    FormControlLabel, 
    Grid, 
    Box, 
    Typography,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    IconButton,
    Collapse
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';

// Reusable Setting Item Component with help tooltip
const SettingItem = ({ helpText, children }) => {
    const [showHelp, setShowHelp] = React.useState(false);
    
    return (
        <Box sx={{ 
            width: '100%',
            mb: { xs: 1, sm: 1.5 }
        }}>
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1
            }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
                {helpText && (
                    <IconButton 
                        size="small" 
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{ 
                            mt: 0.5,
                            color: 'action.active',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            {helpText && (
                <Collapse in={showHelp}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            color: 'text.secondary',
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            p: 1,
                            mt: 0.5,
                            lineHeight: 1.4
                        }}
                    >
                        {helpText}
                    </Typography>
                </Collapse>
            )}
        </Box>
    );
};

// Section Card Component
const SettingsSection = ({ title, icon: Icon, children }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    return (
        <Card 
            elevation={0}
            sx={{ 
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
            }}
        >
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1.25, sm: 1.5 },
                    bgcolor: 'grey.50',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}
            >
                {Icon && <Icon sx={{ fontSize: { xs: 18, sm: 20 }, color: 'primary.main' }} />}
                <Typography 
                    variant="subtitle2" 
                    sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '0.9rem' }
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <CardContent sx={{ 
                px: { xs: 2, sm: 2.5 },
                py: { xs: 2, sm: 2 },
                '&:last-child': { pb: { xs: 2, sm: 2 } }
            }}>
                {children}
            </CardContent>
        </Card>
    );
};

// Responsive TextField Component
const ResponsiveTextField = ({ name, value, onChange, label, disabled, helpText }) => {
    return (
        <SettingItem helpText={helpText}>
            <TextField
                fullWidth
                size="medium"
                label={label}
                name={name}
                value={value || ''}
                onChange={onChange}
                variant="outlined"
                disabled={disabled}
                sx={{
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '1rem', sm: '0.95rem' },
                        py: { xs: 1.5, sm: 1.25 }
                    },
                    '& .MuiInputLabel-root': {
                        fontSize: { xs: '0.95rem', sm: '0.9rem' }
                    }
                }}
            />
        </SettingItem>
    );
};

// Responsive Checkbox Component
const ResponsiveCheckbox = ({ name, checked, onChange, label, disabled, helpText }) => {
    return (
        <SettingItem helpText={helpText}>
            <FormControlLabel
                control={
                    <Checkbox 
                        name={name}
                        checked={checked || false}
                        onChange={onChange}
                        disabled={disabled}
                        sx={{
                            '& .MuiSvgIcon-root': {
                                fontSize: { xs: 24, sm: 24 }
                            }
                        }}
                    />
                }
                label={
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: { xs: '0.875rem', sm: '0.9rem' },
                            color: disabled ? 'text.disabled' : 'text.primary'
                        }}
                    >
                        {label}
                    </Typography>
                }
                sx={{
                    m: 0,
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                        flex: 1
                    }
                }}
            />
        </SettingItem>
    );
};

const BasicDetailsTab = ({ settings, handleInputChange, kioskTypes, updateKioskUI }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Basic Information Section */}
            <SettingsSection title="Basic Information" icon={SettingsIcon}>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="OrganisationName"
                            value={settings.OrganisationName}
                            onChange={handleInputChange}
                            label="Kiosk Name"
                            helpText="The name that identifies this kiosk in your account"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveTextField
                            name="SettingTitleText"
                            value={settings.SettingTitleText}
                            onChange={handleInputChange}
                            label="Title Text"
                            helpText="The title text displayed on the kiosk screen"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SettingItem helpText="The behaviour of the device. Changing this will reset any customisations made on the kiosk.">
                            <FormControl variant="outlined" fullWidth>
                                <InputLabel id="kiosk-type-label">Device Type</InputLabel>
                                <Select
                                    labelId="kiosk-type-label"
                                    value={settings.KioskTypeID || ''}
                                    onChange={(e) => {
                                        if (settings.SettingCustomKioskUI) {
                                            alert('You cannot change this because you have customised the UI. Unset the custom kiosk UI option first before changing this.');
                                            return;
                                        }
                                        handleInputChange(e);
                                        updateKioskUI(e);
                                    }}
                                    label="Device Type"
                                    name="KioskTypeID"
                                    sx={{
                                        '& .MuiSelect-select': {
                                            fontSize: { xs: '1rem', sm: '0.95rem' },
                                            py: { xs: 1.5, sm: 1.25 }
                                        }
                                    }}
                                >
                                    {kioskTypes.map((kioskType) => (
                                        <MenuItem key={kioskType.id} value={kioskType.id}>
                                            {kioskType.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </SettingItem>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <ResponsiveCheckbox
                            name="SettingIncludeVisitorButton"
                            checked={settings.SettingIncludeVisitorButton}
                            onChange={handleInputChange}
                            label="Include Visitor Button"
                            helpText="Shows the Visitor option (button) on the Kiosk screen"
                        />
                    </Grid>
                </Grid>
            </SettingsSection>

            {/* Device Types Info Section */}
            <SettingsSection title="Device Type Reference" icon={InfoIcon}>
                <Box sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                    color: 'text.secondary',
                    '& ul': {
                        m: 0,
                        pl: { xs: 2.5, sm: 3 },
                        '& li': {
                            mb: 1.5,
                            '&:last-child': { mb: 0 }
                        }
                    },
                    '& strong': {
                        color: 'text.primary',
                        fontWeight: 600
                    }
                }}>
                    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.primary' }}>
                        Available device types:
                    </Typography>
                    <ul>
                        <li>
                            <strong>Visitor entry kiosk</strong> — User interface for visitors to request access to your property or land.
                        </li>
                        <li>
                            <strong>Assistance</strong> — Button to request assistance. Use cases: bedside care, kiosk support, general assistance.
                        </li>
                        <li>
                            <strong>Dedicated ANPR</strong> — No user interface; acts as ANPR camera, CCTV device and proactive communicator.
                        </li>
                        <li>
                            <strong>Non-interactive</strong> — No user interface; acts as CCTV and/or proactive communicator. Typical use: CCTV, pet or baby monitor.
                        </li>
                        <li>
                            <strong>PIN pad</strong> — Acts as PIN pad, CCTV device and proactive communicator. Typical use: property/land exit keypad.
                        </li>
                    </ul>
                </Box>
            </SettingsSection>
        </Box>
    );
};

export default BasicDetailsTab;
